import React, { CSSProperties, FC, useMemo } from 'react';
import type { TokenColor } from '../../setup';
import { IconName, IconProps } from '../Icon';
import { IconDropdown, IconDropdownProps } from '../IconDropdown/IconDropdown';
import { BpDesignSystem } from '../../types';
import { useIntlBds } from '../../translations/BdsIntlProvider';
import { Menu, MenuListProps, MenuItemProps } from '../Menu';

export interface OverflowAction {
  dataTest?: string;
  label: React.ReactNode;
  subLabel?: React.ReactNode;
  run: () => void;
  icon?: IconName;
  color?: TokenColor;
  disabled?: boolean;
  hideOnDisabled?: boolean;
}

export type OverFlowMenuProps = BpDesignSystem.PropsWithChildren<
  Pick<IconDropdownProps, 'disabled' | 'iconSize' | 'iconName' | 'iconStyle'> & {
    /*! you can compose menu with children or actions */
    actions?: OverflowAction[];
    onOpen?: () => void;
    onClose?: () => void;
    buttonStyle?: CSSProperties;
    dataTest?: string;
    className?: string;
    disabledMessage?: string;
    tooltipText?: string;
    slotProps?: {
      MenuList?: Omit<MenuListProps, 'children'>;
      MenuItem?: Omit<MenuItemProps<'li'>, 'children'>;
      MenuItemIcon?: Omit<IconProps, 'children' | 'name'>;
    };
  }
>;

export const OverFlowMenu: FC<React.PropsWithChildren<OverFlowMenuProps>> = ({
  className,
  buttonStyle,
  actions,
  disabled,
  onOpen,
  onClose,
  dataTest,
  children,
  disabledMessage,
  tooltipText,
  slotProps,
  ...props
}) => {
  const { intl } = useIntlBds();
  const visibleActions = useMemo(
    () => (actions ? getVisibleActionsOrShowDefaultOnEmpty(actions, intl.OverFlowMenu.noActions.label) : []),
    [actions, intl.OverFlowMenu.noActions.label]
  );

  return (
    <IconDropdown
      tooltip={tooltipText}
      disabledMessage={disabledMessage}
      disabled={disabled || (!children && !visibleActions?.length)}
      onClose={() => onClose?.()}
      onOpen={() => onOpen?.()}
      dataTest={dataTest ?? 'overflow-menu-button'}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right'
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right'
      }}
      iconStyle={buttonStyle}
      iconClassName={className}
      {...props}
    >
      <Menu.List
        dataTest="option__dropdown"
        onClick={(event) => {
          if (event.target === event.currentTarget) {
            event.preventDefault();
            event.stopPropagation();
          }
        }}
        {...slotProps?.MenuList}
      >
        {children ??
          visibleActions.map((action) => {
            const isDisabled = !!action.disabled;
            return (
              <Menu.Item
                key={`${action.dataTest ?? ''}${action.icon ?? ''}${action.label?.toString()}`}
                onClick={(event) => {
                  event.preventDefault();
                  if (!isDisabled) {
                    action.run();
                  }
                }}
                secondaryText={action.subLabel}
                disabled={isDisabled}
                dataTest={`overflow__action__${action.dataTest}`}
                style={{ color: action.color }}
                className={isDisabled ? 'disabled' : undefined}
                startIcon={action.icon}
                {...slotProps?.MenuItem}
                slotProps={{
                  startIcon: {
                    color: action.color,
                    ...slotProps?.MenuItemIcon
                  },
                  endIcon: {
                    color: action.color
                  },
                  listItemText: {
                    color: action.color
                  }
                }}
              >
                {action.label}
              </Menu.Item>
            );
          })}
      </Menu.List>
    </IconDropdown>
  );
};

const DEFAULT_NO_OVERFLOW_ACTION = (noActionsLabel: string): OverflowAction => ({
  dataTest: 'no_overflow_action',
  label: noActionsLabel,
  run: () => {}
});

const getVisibleActionsOrShowDefaultOnEmpty = (allActions: OverflowAction[], noActionsLabel: string) => {
  const visible = allActions?.filter((action) => {
    const shouldBeHidden = action.hideOnDisabled && !!action.disabled;
    return !shouldBeHidden;
  });

  if (visible?.length === 0) {
    visible.push(DEFAULT_NO_OVERFLOW_ACTION(noActionsLabel));
  }

  return visible ?? [];
};
